import type { ProductFeatures } from '@integration-layer/ampliance/schemas/product-features-schema.localized'

/**
 * Composable for fetching and managing product features list.
 *
 * @param {string} deliveryKey - The key used to fetch specific product features.
 * @returns {Object} An object containing the product features list.
 * @property {Ref<ProductFeatures | null>} productFeaturesList - Reactive state of product features.
 *
 * @example
 * const { productFeaturesList } = useProductFeaturesList('product-care-items')
 *
 * @remarks
 * This composable handles fetching product features with fallback mechanisms.
 * It supports localized content retrieval and handles potential fetch errors.
 */
export const useProductFeaturesList = (deliveryKey: string) => {
  const localePath = useLocalePath()
  const route = useRoute()
  const { country } = useRouteHelper()

  /**
   * Reactive state to store product features list.
   *
   * @type {Ref<ProductFeatures | null>}
   */
  const productFeaturesList: Ref<ProductFeatures | null> = useState(
    'productFeaturesList',
    () => null
  )

  /**
   * Computed delivery key to trigger reactivity.
   *
   * @type {ComputedRef<string>}
   */
  const deliveryKeyComp = computed(() => deliveryKey)

  /**
   * Fetches product features from the CMS API.
   *
   * @param {string} key - The delivery key for fetching features.
   * @returns {Promise<any>} A promise resolving to the fetched product features.
   * @throws {Error} If there's an issue fetching the product features.
   *
   * @private
   */
  const fetchProductFeatures = async (key: string) => {
    const query = {
      locale: localePath(''),
      vse: route.query.vse,
      contentId: route.query.contentId,
      deliveryKey: key,
    }

    try {
      return await $fetch('/api/cms/getProductFeaturesList', { query })
    } catch (error) {
      console.error(`Error fetching product features for key: ${key}`, error)
      throw error // rethrow the error for the caller to handle
    }
  }

  /**
   * Loads product features with a fallback mechanism.
   *
   * @async
   * @throws {Error} If both primary and fallback fetches fail.
   *
   * @remarks
   * This method attempts to fetch features with a country-specific key first,
   * then falls back to the original delivery key if the first attempt fails.
   */
  const loadProductFeatures = async () => {
    // Commented out country-specific fallback for now
    // try {
    //   const response = await fetchProductFeatures(`${deliveryKey}-${country}`)
    //   productFeaturesList.value = response.content
    // } catch {
    try {
      const fallbackResponse = await fetchProductFeatures(deliveryKey)
      productFeaturesList.value = fallbackResponse.content
    } catch (error) {
      productFeaturesList.value = null
      console.error('Failed to load product features:', error)
    }
    // }
  }

  /**
   * Watches the delivery key and triggers feature loading.
   *
   * @remarks
   * Automatically loads product features when the delivery key changes,
   * with an immediate initial load.
   */
  watch(
    deliveryKeyComp,
    () => {
      loadProductFeatures()
    },
    { immediate: true }
  )

  return {
    productFeaturesList,
  }
}
